import { GlobalEmptyState } from '~publish/legacy/shared-components'

import {
  useAccount,
  useCurrentOrganization,
  useHasOrganizationAction,
} from '../../../../contexts/AccountContext'
import { getTypeContent } from '../../utils'
import type { MissingAccessTranslations } from '../../utils'

import type { JSX } from 'react'

type MissingAccessPageProps = {
  switchOrganization: (orgId: string) => void
  translations: MissingAccessTranslations & {
    altText: string
    heading: string
  }
}

type LegacyOrganization = {
  id: string
  name: string
  billing: {
    canAccessPublishing: boolean
  }
}

const isLegacyOrganization = (value: unknown): value is LegacyOrganization => {
  if (typeof value !== 'object' || value === null) return false
  if (
    !('id' in value) ||
    typeof value.id !== 'string' ||
    !('name' in value) ||
    typeof value.name !== 'string' ||
    !('billing' in value) ||
    typeof value.billing !== 'object' ||
    value.billing === null ||
    !('canAccessPublishing' in value.billing)
  ) {
    return false
  }

  return typeof value.billing.canAccessPublishing === 'boolean'
}

const MissingAccessPage = ({
  switchOrganization,
  translations,
}: MissingAccessPageProps): JSX.Element => {
  const user = useAccount()
  const legacyOrganizations: unknown =
    typeof user === 'object' && user !== null && 'organizations' in user
      ? user.organizations
      : undefined
  const orgWithAccess = Array.isArray(legacyOrganizations)
    ? legacyOrganizations.find(
        (organization) =>
          isLegacyOrganization(organization) &&
          organization.billing.canAccessPublishing,
      )
    : undefined

  const organization = useCurrentOrganization()
  const orgName = organization?.name ?? null
  // The "admin" content asks the user to start a plan, so it is for users
  // who can manage billing.
  const canManageBilling = useHasOrganizationAction('manageBilling')
  const content = getTypeContent({
    switchOrganization,
    translations,
    canManageBilling,
    orgWithAccess,
    orgName,
  })

  return (
    <GlobalEmptyState
      imageSrc="https://buffer-publish.s3.amazonaws.com/images/illustration-publishing.png"
      altText={translations.altText}
      header={translations.heading}
      description={content.description}
      primaryAction={'primaryAction' in content ? content.primaryAction : null}
      secondaryAction={content.secondaryAction}
    />
  )
}

export default MissingAccessPage
